import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { AuthService } from 'auth';
import { EnvService } from 'frontend-environment';
import { catchError, filter, of, take } from 'rxjs';
import { SplitIoService } from 'split-io';

export type DatadogEnv = {
  DD_API_KEY?: string;
  DD_RUM_APP_ID?: string;
  DD_RUM_CLIENT_TOKEN?: string;
  DD_SITE?: string;
  DD_ENV?: string;
  DD_SERVICE?: string;
  VERSION?: string;
};

@Injectable({
  providedIn: 'root',
})
export class DataDogService {
  private envService = inject<EnvService<DatadogEnv>>(EnvService);
  private authService = inject(AuthService);
  private httpClient = inject(HttpClient);
  private splitIoService = inject(SplitIoService);

  // This method exists to prevent tree-shaking of the service
  dummy() {} // eslint-disable-line @typescript-eslint/no-empty-function

  constructor() {
    this.splitIoService
      .getSplitTreatment$('datadog_instrumentation_enabled')
      .pipe(
        filter((enabled) => enabled !== 'control'),
        take(1),
        filter((enabled) => enabled === 'on'),
      )
      .subscribe(() => {
        const ddRumConfig = this.rumConfig();
        if (ddRumConfig) {
          this.httpClient
            .get('/appVersion.txt', {
              responseType: 'text',
            })
            .pipe(catchError(() => of('UNKNOWN')))
            .subscribe((version) => {
              datadogRum.init({
                version,
                sessionSampleRate: 100,
                sessionReplaySampleRate: 100,
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                defaultPrivacyLevel: 'allow',
                trackSessionAcrossSubdomains: true,
                allowedTracingUrls: [
                  /https:\/\/.*\.terminus\.(services|ninja)/,
                ],
                ...ddRumConfig,
              });

              console.log('DD RUM Initialized');
            });

          this.authService.actualUserOktaData$.subscribe((userData) => {
            if (userData) {
              datadogRum.setUser({
                id: userData.id,
                name: userData.name,
                email: userData.email,
              });
            }
          });

          datadogRum.startSessionReplayRecording();
        }

        const ddLogConfig = this.loggingConfig();
        if (ddLogConfig) {
          datadogLogs.init({
            forwardErrorsToLogs: true,
            forwardReports: 'all',
            forwardConsoleLogs: 'all',
            sessionSampleRate: 100,
            trackSessionAcrossSubdomains: true,
            ...ddLogConfig,
          });
        }
      });
  }

  loggingConfig() {
    if (this.envService.DD_API_KEY && this.envService.DD_SITE) {
      return {
        site: this.envService.DD_SITE,
        clientToken: this.envService.DD_API_KEY,
        env: this.envService.DD_ENV || '',
        service: this.envService.DD_SERVICE || '',
      };
    }

    console.log(
      'Datadog API Key or Site are not configured, unable to setup logging',
    );
    return null;
  }

  rumConfig() {
    if (
      this.envService.DD_RUM_APP_ID &&
      this.envService.DD_RUM_CLIENT_TOKEN &&
      this.envService.DD_SITE &&
      this.envService.DD_ENV &&
      this.envService.DD_SERVICE
    ) {
      return {
        applicationId: this.envService.DD_RUM_APP_ID,
        clientToken: this.envService.DD_RUM_CLIENT_TOKEN,
        site: this.envService.DD_SITE,
        env: this.envService.DD_ENV,
        service: this.envService.DD_SERVICE,
      };
    }

    console.log('Missing Datadog RUM config. Skipping initialization.');
    return null;
  }
}
